import {faPen} from '@fortawesome/pro-regular-svg-icons/faPen';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import {useEditButton} from '@refinedev/core';
import type {EditButtonProps} from '@refinedev/mui';
import React, {FC} from 'react';

/**
 * `<EditButton>` uses uses Material UI {@link https://mui.com/components/buttons/ `<Button>`} component.
 * It uses the {@link https://refine.dev/docs/api-reference/core/hooks/navigation/useNavigation#edit `edit`} method from {@link https://refine.dev/docs/api-reference/core/hooks/navigation/useNavigation `useNavigation`} under the hood.
 * It can be useful when redirecting the app to the edit page with the record id route of resource}.
 *
 * @see {@link https://refine.dev/docs/api-reference/mui/components/buttons/edit-button} for more details.
 */
export const EditButton: FC<EditButtonProps> = ({
  resource: resourceNameFromProps,
  resourceNameOrRouteName,
  recordItemId,
  hideText = false,
  accessControl,
  meta,
  children,
  onClick,
  ...rest
}) => {
  const {to, label, title, hidden, disabled, LinkComponent} = useEditButton({
    resource: resourceNameFromProps ?? resourceNameOrRouteName,
    id: recordItemId,
    accessControl,
    meta,
  });

  const isDisabled = disabled || rest.disabled;
  const isHidden = hidden || rest.hidden;

  if (isHidden) return null;

  const {sx, ...restProps} = rest;

  const icon = <FontAwesomeIcon icon={faPen} />;

  return (
    <LinkComponent
      to={to}
      replace={false}
      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        if (onClick) {
          e.preventDefault();
          onClick(e);
        }
      }}
      style={{textDecoration: 'none', color: 'inherit'}}
    >
      <Button
        disabled={isDisabled}
        startIcon={!hideText && icon}
        title={title}
        sx={{minWidth: 0, ...sx}}
        {...restProps}
      >
        {hideText ? icon : (children ?? label)}
      </Button>
    </LinkComponent>
  );
};
